import React, { useEffect, useMemo, useRef, useState } from "react";

import {
  faExclamationTriangle,
  faTimes,
} from "@fortawesome/pro-regular-svg-icons";
import {
  Controller,
  FieldError,
  FieldErrorsImpl,
  Merge,
  useFormContext,
} from "react-hook-form";
import { twJoin } from "tailwind-merge";
import { useOnClickOutside } from "usehooks-ts";

import { Icon } from "src/base-components/Icon";
import { Pill } from "src/base-components/Pill";
import { Tooltip } from "src/design-system/Tooltip";
import { SchemaEnumItemInput } from "src/schema/enumEditor/SchemaEnumItemInput";
import {
  getEnumError,
  isValidEnumOptionInput,
  validateUniqueEnumOptions,
} from "src/schema/enumUtils";
import { EnumValueObject, SchemaUI } from "src/schema/schemaMappingUtils";

export type SchemaEnumErrorStatus = Merge<
  FieldError,
  (FieldErrorsImpl<EnumValueObject> | undefined)[]
>;

type Props = {
  onDelete: () => void;
  readonly?: boolean;
  deletable: boolean;
  propertyIndex: number;
  enumIndex: number;
  errorStatus?: SchemaEnumErrorStatus;
};

export const SchemaEnumItem: React.FC<Props> = ({
  readonly = false,
  onDelete,
  propertyIndex,
  enumIndex,
  deletable,
}) => {
  const { control, getValues, trigger } = useFormContext<SchemaUI>();
  const ref = useRef<HTMLLIElement | null>(null);
  const [isEditing, setIsEditing] = useState(false);
  const fieldPath =
    `properties.${propertyIndex}.enum.${enumIndex}.value` as const;

  const validators = useMemo(
    () => ({
      unique: (value: string) =>
        validateUniqueEnumOptions(
          value,
          getValues(`properties.${propertyIndex}.enum`),
        ),
      validValue: isValidEnumOptionInput,
    }),
    [propertyIndex, getValues],
  );

  const isDeleteDisabled = readonly || !deletable;

  useOnClickOutside(ref, () => setIsEditing(false));

  useEffect(() => {
    trigger(fieldPath);
  }, [fieldPath, trigger]);

  return (
    <li
      ref={ref}
      className="flex flex-row items-center gap-x-2"
      data-loc={`schema-enum-pill-${propertyIndex}`}
    >
      {isEditing && (
        <Controller
          control={control}
          name={fieldPath}
          render={({ field }) => (
            <SchemaEnumItemInput
              dataLoc={`schema-enum-input-${propertyIndex}-${enumIndex}`}
              disabled={readonly}
              value={field.value}
              onBlur={field.onBlur}
              onCancel={() => setIsEditing(false)}
              onCommit={(value) => {
                setIsEditing(false);
                if (value !== field.value) {
                  field.onChange(value);
                }
              }}
            />
          )}
          rules={{
            validate: validators,
          }}
        />
      )}

      {!isEditing && (
        <div className="flex h-[34px] items-center">
          <Controller
            control={control}
            name={fieldPath}
            render={({ field, fieldState: { error } }) => {
              const enumError = getEnumError(error?.type ?? "");
              return (
                <Tooltip
                  body={enumError?.description}
                  disabled={!error}
                  title={enumError?.title}
                  asChild
                >
                  <Pill
                    dataLoc={`schema-enum-item-${propertyIndex}-${enumIndex}`}
                    size="sm"
                    variant="gray"
                    onClick={() => setIsEditing(true)}
                  >
                    {error && (
                      <Pill.Icon
                        color="text-red-400"
                        icon={faExclamationTriangle}
                      />
                    )}
                    <Pill.Text>{field.value}</Pill.Text>
                    <Icon
                      color={twJoin(
                        isDeleteDisabled ? "text-gray-300" : "text-gray-400",
                      )}
                      dataLoc="enum-field-delete"
                      disabled={isDeleteDisabled}
                      icon={faTimes}
                      size="xs"
                      onClick={onDelete}
                    />
                  </Pill>
                </Tooltip>
              );
            }}
            rules={{
              validate: validators,
            }}
          />
        </div>
      )}
    </li>
  );
};
